import React, { useEffect, lazy, Suspense } from 'react';
import { Route, Switch, Redirect, useHistory, useLocation } from 'react-router-dom';
import { connect, useSelector } from 'react-redux';
import axios from 'axios';
import { parse } from 'query-string';
import { ThemeProvider } from 'styled-components/macro';
import { theme } from 'styles/theme';
import Cookies from 'js-cookie';
import Tracking from 'components/Tracking';
import { GettingStarted } from 'components/Onboarding/PreConversion/Interstitial/sections/GettingStarted';
import { PageWrapper } from 'App.styled';
import { useKetchScript } from 'utils/ketch/script';
import { isReleaseOrProduction } from 'utils/common';
import { Spinner } from '@cerebral-inc/design-system';
import { Store } from 'types/store';
import { usePortalMaintenanceRedirect } from 'features/usePortalMaintenanceRedirect';
import {
  GUEST_APPOINTMENT_KEY,
  SEGMENT_FLOW_TRACKED_KEY,
  ZOCDOC_APPOINTMENT_KEY
} from 'components/Onboarding/shared/hooks/guestAppointment/constants';
import { Logout, Signup as UniversalLoginSignUp, MobileLogin } from './components/UniversalLogin';
import TimerModal, { showTimerModal } from './components/OutstandingTasksModal/TimerModal';
import ErrorBoundary, { ErrorPage } from './ErrorBoundary';
import 'bootstrap/dist/css/bootstrap.min.css';
import './styles/cerebral.scss';
import ApiTester from './components/ApiTester';
import initRum from './utils/datadog-rum';
import segment from './utils/segment';

import strings from './localization';
import { writeClickIdsToCookie } from './components/Onboarding/helpers';
import { extractMSClickId } from './utils/hooks/useAnalyticsPlatform';

axios.defaults.baseURL = process.env.REACT_APP_API_SERVER_URL;
// Jacob's right
// Add custom header to sent for track segment user on server
const segmentAID = localStorage.getItem('ajs_anonymous_id');

if (segmentAID) {
  axios.defaults.headers.common['X-AnonymousId'] = segmentAID.replace(/['"]+/g, '');
}

const Patient = lazy(() => import('./containers/patient'));
const DirectRoutes = lazy(() => import('./components/DirectRoutes'));
const MemberSignIn = lazy(() => import('./components/SignIn'));
const UniversalLogin = lazy(() => import('./components/UniversalLogin'));
const SignUp = lazy(() => import('./components/sign_up/sign_up_segment_container'));
const PreSignUpManager = lazy(() => import('components/Onboarding/PreSignupFlow/QuestionManager'));
const AccountCreation = lazy(() => import('components/Onboarding/ClinSearchSchedule/Interstitials/AccountCreation'));

const App = () => {
  const isSignedIn = useSelector((state: Store) => state.global_reducer.current_user.is_signedIn);
  const history = useHistory();
  const { pathname, search } = useLocation();
  const country = 'us';
  const UrlParams = parse(search);
  const appsflyerObj = segment.urlParamsToContext(UrlParams);
  const isFromMobileApp = sessionStorage.getItem('fromMobileUser') !== null;

  useEffect(() => {
    initRum();

    // Check utm_content in URL parameter to set into localStorage
    const params = new URLSearchParams(window.location.search);
    if (params.get('utm_content')) {
      window.localStorage.setItem('utm_content', params.get('utm_content') as string);
    }
    if (params.get('datadog_synthetic_test')) {
      window.localStorage.setItem('datadog_synthetic_test', params.get('datadog_synthetic_test') as string);
    }
    if (params.get('enable_segment_track') && !Cookies.get('enable_segment_track')) {
      Cookies.set('enable_segment_track', 'true', { expires: 1 });
    }
    // Check onboarding flow in URL parameter to set into Cookies
    if (params.get('ob_flow')) {
      sessionStorage.setItem('ob_flow', params.get('ob_flow') as string);
    }
    // Guest Appointment Id
    if (
      params.get(GUEST_APPOINTMENT_KEY) &&
      !['undefined', 'null'].includes(params.get(GUEST_APPOINTMENT_KEY) as string)
    ) {
      sessionStorage.setItem(GUEST_APPOINTMENT_KEY, params.get(GUEST_APPOINTMENT_KEY) as string);
    }

    // Zocdoc appointment id
    if (
      params.get(ZOCDOC_APPOINTMENT_KEY) &&
      !['undefined', 'null'].includes(params.get(ZOCDOC_APPOINTMENT_KEY) as string)
    ) {
      sessionStorage.setItem(ZOCDOC_APPOINTMENT_KEY, params.get(ZOCDOC_APPOINTMENT_KEY) as string);
    }

    // Guest Appointment Payment Option
    if (params.get('payment')) {
      sessionStorage.setItem('guest_appointment_payment_flow', params.get('payment') as string);
    }

    extractMSClickId(params);
  }, []);

  // Remove SEGMENT_FLOW_TRACKED_KEY on new session
  sessionStorage.removeItem(SEGMENT_FLOW_TRACKED_KEY);

  useEffect(() => {
    writeClickIdsToCookie();
  }, []);

  useKetchScript();
  usePortalMaintenanceRedirect();

  useEffect(() => {
    segment.page(pathname, undefined, appsflyerObj);
    window.scrollTo(0, 0);
  }, [pathname]);

  strings.setLanguage(country);

  const onLogout = () => {
    history.push('/logout');
  };

  return (
    <ThemeProvider theme={theme}>
      <Tracking />

      <div className="App d-flex justify-content-center">
        <PageWrapper>
          {isSignedIn && !isFromMobileApp && showTimerModal(pathname) && <TimerModal onLogout={onLogout} />}
          <ErrorBoundary>
            <Suspense fallback={<Spinner variant="screen" hideScreenOverlay />}>
              <Switch>
                <Route
                  path="/patient/sign-up"
                  render={() => {
                    return <Redirect to={`/signup${search}`} />;
                  }}
                />
                <Route
                  path="/patient"
                  render={() => {
                    return <Patient />;
                  }}
                />
                {isReleaseOrProduction() ? (
                  <Route path="/member_sign_in" component={UniversalLogin} />
                ) : (
                  <Route path="/member_sign_in" component={MemberSignIn} />
                )}
                <Route path="/logout" component={Logout} />
                <Route path="/mobile-login" component={MobileLogin} />
                <Route
                  path="/signup"
                  render={() => {
                    if (isReleaseOrProduction()) {
                      return <UniversalLoginSignUp />;
                    }
                    return <SignUp />;
                  }}
                />
                <Route path="/appointment" component={DirectRoutes} />

                <Route path="/pre-signup/:questionName?" component={PreSignUpManager} />

                <Route path="/getting-started" component={GettingStarted} />
                <Route path="/error" component={ErrorPage} />
                <Route path="/account-creation" component={AccountCreation} />
                {isReleaseOrProduction() ? <Route component={UniversalLogin} /> : <Route component={MemberSignIn} />}
              </Switch>
            </Suspense>
          </ErrorBoundary>
          {process.env.REACT_APP_INSTALL_API_TESTER !== 'featureFlags.auth_userlogin_auth0' && <ApiTester />}
        </PageWrapper>
      </div>
    </ThemeProvider>
  );
};

const mapStateToProps = (state: Store) => ({
  isSignedIn: state.global_reducer.current_user.is_signedIn
});

export default connect(mapStateToProps, {})(App);
